import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatFormField, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { isNil } from 'lodash-es';
import {
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  map,
  of,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { DataTableContainerComponent } from '../../components/data-table-container/data-table-container.component';
import { ConfirmHandler } from '../../components/dialog/dialog.model';
import { DialogService } from '../../components/dialog/dialog.service';
import { HasPermissionDirective } from '@ipg/ngx-frontend-toolset';
import { ContactResponseModel } from '../../models/contacts.model';
import { ContactService } from '../../services/contact.service';
import { TableLimitService } from '../../services/table-limit.service';
import {
  SaveContactDialogComponent,
  SaveContactDialogResult,
} from './components/save-contact-dialog/save-contact-dialog.component';

@Component({
  selector: 'app-settings-contacts',
  imports: [
    MatTableModule,
    MatButton,
    MatIcon,
    MatIconButton,
    MatPaginator,
    MatSort,
    MatSortHeader,
    MatTooltip,
    MatFormField,
    MatInput,
    MatSuffix,
    MatPrefix,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    DataTableContainerComponent,
    DatePipe,
    FormsModule,
    HasPermissionDirective,
  ],
  templateUrl: './settings-contacts.component.html',
  styleUrl: './settings-contacts.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsContactsComponent {
  private readonly _contactService = inject(ContactService);
  private readonly _dialog = inject(DialogService);
  private readonly _tableLimit = inject(TableLimitService);

  private readonly _fetch$ = new Subject<void>();

  private readonly _keywordInput$ = new Subject<string | null>();
  private readonly _debouncedKeyword$ = this._keywordInput$.pipe(
    debounceTime(300),
    distinctUntilChanged(),
    tap(() => this._query.update((currentState) => ({ ...currentState, page: 1 })))
  );

  protected readonly keyword = signal<string | null>(null);

  private readonly _hasLoadedData = signal<boolean>(false);

  private readonly _query = signal({
    page: 1,
    limit: this._tableLimit.getLimit(),
  });

  private readonly _sort = signal({
    sortBy: 'name',
    sortOrder: 'asc',
  });

  private readonly _params$ = combineLatest({
    query: toObservable(this._query),
    sort: toObservable(this._sort),
    keyword: this._debouncedKeyword$.pipe(startWith(this.keyword())),
    __fetch: this._fetch$.pipe(startWith(undefined)),
  }).pipe(
    map(({ query, sort, keyword }) => ({
      ...query,
      ...sort,
      ...(keyword ? { q: keyword.trim() } : null),
    })),
    debounceTime(0)
  );

  private readonly _paginatedContacts$ = this._params$.pipe(
    switchMap((params) => this._contactService.getAll({ params })),
    tap(() => this._hasLoadedData.set(true))
  );

  private readonly _contacts$ = this._paginatedContacts$.pipe(
    map(({ items }) => items)
  );

  private readonly _paginator = viewChild.required(MatPaginator);

  protected readonly displayedColumns = [
    'name',
    'phoneNumber',
    'email',
    'createdAt',
    'updatedAt',
    'actions',
  ];

  protected readonly dataSource = toSignal(this._contacts$, { initialValue: [] });

  protected readonly hasNoData = computed(() => this.dataSource().length === 0);

  protected readonly pageSize = computed(() => this._query().limit);

  protected readonly isSearchDisabled = computed(() => !this._hasLoadedData());

  handleContactAdd() {
    const dialogRef = this.openSaveContactDialog(
      (result) =>
        of(result).pipe(
          filter((result) => !isNil(result)),
          switchMap((result) =>
            this._contactService.create(result).pipe(
              catchError(() => {
                this._dialog.alert({
                  title: 'Foutmelding',
                  message:
                    'Er is iets misgegaan bij het toevoegen van een contact. Probeer het later nog eens',
                  confirmText: 'OK',
                });

                dialogRef.close();

                return EMPTY;
              })
            )
          ),
          finalize(() => this._fetch$.next())
        ),
      {
        title: 'Nieuw contact toevoegen',
      }
    );
  }

  handleContactUpdate(value: ContactResponseModel) {
    const dialogRef = this.openSaveContactDialog(
      (result) =>
        of(result).pipe(
          filter((result) => !isNil(result)),
          switchMap((result) =>
            this._contactService.update(value._id, result).pipe(
              catchError(() => {
                this._dialog.alert({
                  title: 'Foutmelding',
                  message:
                    'Er is iets misgegaan bij het bewerken van het contact. Probeer het later nog eens',
                  confirmText: 'OK',
                });

                dialogRef.close();

                return EMPTY;
              })
            )
          ),
          finalize(() => this._fetch$.next())
        ),
      {
        title: 'Contact bewerken',
        data: value,
      }
    );
  }

  handleContactDelete(value: ContactResponseModel) {
    this._dialog.confirm({
      title: `${value.name} verwijderen?`,
      message: '',
      confirmText: 'Verwijderen',
      cancelText: 'Annuleren',
      onConfirm: () =>
        this._contactService.delete(value._id).pipe(
          catchError(() => {
            this._dialog.alert({
              title: 'Foutmelding',
              message:
                'Er is iets misgegaan bij het verwijderen van het contact. Probeer het later nog eens',
              confirmText: 'OK',
            });

            return EMPTY;
          }),
          finalize(() => this._fetch$.next())
        ),
    });
  }

  handlePageChange({ pageIndex, pageSize }: PageEvent) {
    this._tableLimit.saveLimit(pageSize);
    this._query.update((currentState) => ({
      ...currentState,
      page: pageIndex + 1,
      limit: pageSize,
    }));
  }

  handleSortChange({ active, direction }: Sort) {
    this._sort.update((currentState) => ({
      ...currentState,
      sortBy: active,
      sortOrder: direction,
    }));
  }

  handleSearch(value: string) {
    this.keyword.set(value.trim());
    this._keywordInput$.next(value.trim());
  }

  clearSearch() {
    this.keyword.set(null);
    this._keywordInput$.next(null);
  }

  openSaveContactDialog(
    callback: ConfirmHandler<SaveContactDialogResult>,
    options: {
      title: string;
      data?: ContactResponseModel;
    }
  ) {
    return this._dialog.open(
      SaveContactDialogComponent,
      {
        onConfirm: callback,
        title: options.title,
        contactData: options.data,
      },
      {
        maxWidth: '396px',
        width: '100%',
      }
    );
  }

  private _setPaginator({ page, total }: { page: number; total: number }) {
    this._paginator().pageIndex = page - 1;
    this._paginator().length = total;
  }
}
